import { z } from 'zod';
import { timestampSchema } from './admin.js';
import { type HolidayRegion, holidayRegionSchema } from './calendar/holidays.js';
import { addWorkingDays, countWorkingDays, nearestWorkingDay } from './calendar/working-days.js';
import { lagDaysSchema } from './gantt.js';
import {
  isoDateSchema,
  labelNameSchema,
  statusCategorySchema,
  statusNameSchema,
  wipLimitSchema,
  type StatusCategory,
} from './projects.js';
import { richTextSchema, type RichText } from './rich-text.js';
import { summarizeSubtasks } from './scheduling/summary.js';
import type { TaskDates } from './scheduling/plan-shift.js';
import {
  dependencyTypeSchema,
  estimateHoursSchema,
  TASK_LIST_LIMIT,
  taskPrioritySchema,
  taskTitleSchema,
  taskTypeSchema,
  type TaskPriority,
  type TaskType,
} from './tasks.js';

/**
 * PROJ-06..08, E-29: project templates. A template is a snapshot of a project's workflow, labels,
 * tasks, dependencies and schedule; dates are stored as working-day offsets from the earliest
 * date (day 0) and placed again with the holiday region of the target project.
 */

export const TEMPLATE_MAX_TASKS = TASK_LIST_LIMIT;

/** Problem `type` URIs of templates, so the UI can show specific messages. */
export const templateProblemTypes = {
  nameTaken: 'urn:pm-tool:problem:template-name-taken',
  projectHasTasks: 'urn:pm-tool:problem:template-project-has-tasks',
  tooLarge: 'urn:pm-tool:problem:template-too-large',
  invalidAnchor: 'urn:pm-tool:problem:template-invalid-anchor',
} as const;

// --- Content (stored as jsonb, docs/03 project_templates) --------------------------------------

const localKeySchema = z.string().min(1).max(40);
/**
 * Lowercase `#rrggbb` without a transform: the content is also a response body, and response
 * serialization cannot encode transforms. `buildTemplateContent` lowercases.
 */
const storedColorSchema = z.string().regex(/^#[0-9a-f]{6}$/);
/** Working days from day 0; ten years are plenty. */
const offsetSchema = z.number().int().min(0).max(2600);

export const templateStatusSchema = z.object({
  name: statusNameSchema,
  category: statusCategorySchema,
  color: storedColorSchema,
  wipLimit: wipLimitSchema.nullable(),
});

export const templateLabelSchema = z.object({
  key: localKeySchema,
  name: labelNameSchema,
  color: storedColorSchema,
});

export const templateTaskSchema = z.object({
  key: localKeySchema,
  parentKey: localKeySchema.nullable(),
  type: taskTypeSchema,
  title: taskTitleSchema,
  description: richTextSchema.nullable(),
  priority: taskPrioritySchema,
  estimateHours: estimateHoursSchema.nullable(),
  labelKeys: z.array(localKeySchema).max(50),
  startOffset: offsetSchema.nullable(),
  dueOffset: offsetSchema.nullable(),
});
export type TemplateTask = z.infer<typeof templateTaskSchema>;

export const templateDependencySchema = z.object({
  predecessorKey: localKeySchema,
  successorKey: localKeySchema,
  type: dependencyTypeSchema,
  lagDays: lagDaysSchema,
});

export const projectTemplateContentSchema = z
  .object({
    version: z.literal(1),
    holidayRegion: holidayRegionSchema,
    statuses: z.array(templateStatusSchema).min(1).max(50),
    labels: z.array(templateLabelSchema).max(200),
    tasks: z.array(templateTaskSchema).max(TEMPLATE_MAX_TASKS),
    dependencies: z.array(templateDependencySchema).max(10_000),
  })
  .superRefine((content, ctx) => {
    for (const issue of contentIssues(content)) ctx.addIssue({ code: 'custom', message: issue });
  });
export type ProjectTemplateContent = z.infer<typeof projectTemplateContentSchema>;

/** Structural rules of the task model (docs/03 tasks, task_dependencies) for a snapshot. */
function contentIssues(content: {
  statuses: readonly { name: string; category: StatusCategory }[];
  labels: readonly { key: string; name: string }[];
  tasks: readonly TemplateTask[];
  dependencies: readonly { predecessorKey: string; successorKey: string }[];
}): string[] {
  const issues: string[] = [];
  if (!content.statuses.some((s) => s.category === 'todo'))
    issues.push('No status of category todo');
  if (new Set(content.statuses.map((s) => s.name.toLowerCase())).size !== content.statuses.length) {
    issues.push('Duplicate status name');
  }
  const labelKeys = new Set(content.labels.map((l) => l.key));
  if (labelKeys.size !== content.labels.length) issues.push('Duplicate label key');

  const seen = new Map<string, TemplateTask>();
  const parents = new Set<string>();
  for (const task of content.tasks) {
    if (seen.has(task.key)) issues.push(`Duplicate task key ${task.key}`);
    if (task.parentKey !== null) {
      const parent = seen.get(task.parentKey);
      // Parents come first; only one level of subtasks, never under a milestone (TASK-01).
      if (parent === undefined || parent.parentKey !== null || parent.type !== 'task') {
        issues.push(`Invalid parent of ${task.key}`);
      }
      if (task.type === 'milestone') issues.push(`Milestone ${task.key} cannot be a subtask`);
      parents.add(task.parentKey);
    }
    if (task.labelKeys.some((key) => !labelKeys.has(key)))
      issues.push(`Unknown label at ${task.key}`);
    if (task.startOffset !== null && task.dueOffset !== null && task.startOffset > task.dueOffset) {
      issues.push(`Start after due date at ${task.key}`);
    }
    if (task.type === 'milestone' && task.startOffset !== task.dueOffset) {
      issues.push(`Milestone ${task.key} needs one date`);
    }
    seen.set(task.key, task);
  }

  const pairs = new Set<string>();
  for (const dep of content.dependencies) {
    const pair = `${dep.predecessorKey}>${dep.successorKey}`;
    if (!seen.has(dep.predecessorKey) || !seen.has(dep.successorKey)) {
      issues.push(`Unknown task in dependency ${pair}`);
    } else if (dep.predecessorKey === dep.successorKey || pairs.has(pair)) {
      issues.push(`Invalid dependency ${pair}`);
    } else if (parents.has(dep.predecessorKey) || parents.has(dep.successorKey)) {
      issues.push(`Dependency on a task with subtasks ${pair}`);
    }
    pairs.add(pair);
  }
  return issues;
}

// --- Building a template from a project (PROJ-06) ----------------------------------------------

export interface TemplateSourceTask {
  id: string;
  parentId: string | null;
  number: number;
  type: TaskType;
  title: string;
  description: RichText | null;
  priority: TaskPriority;
  estimateHours: number | null;
  startDate: string | null;
  dueDate: string | null;
  labelIds: readonly string[];
}

export interface TemplateSource {
  holidayRegion: HolidayRegion;
  statuses: readonly z.input<typeof templateStatusSchema>[];
  labels: readonly { id: string; name: string; color: string }[];
  /** Not deleted tasks of the project. */
  tasks: readonly TemplateSourceTask[];
  dependencies: readonly {
    predecessorId: string;
    successorId: string;
    type: z.infer<typeof dependencyTypeSchema>;
    lagDays: number;
  }[];
}

/**
 * Snapshot of a project. Tasks in the order top-level task by number, followed by its subtasks.
 * Dates are kept only where they are not derived (tasks without dated subtasks); a date on a
 * non-working day counts from the next working day (like GANTT-12). Dependencies to tasks that
 * are missing (deleted) are dropped.
 */
export function buildTemplateContent(source: TemplateSource): ProjectTemplateContent {
  const region = source.holidayRegion;
  const byNumber = [...source.tasks].sort((a, b) => a.number - b.number);
  const childrenOf = new Map<string, TemplateSourceTask[]>();
  for (const task of byNumber) {
    if (task.parentId === null) continue;
    childrenOf.set(task.parentId, [...(childrenOf.get(task.parentId) ?? []), task]);
  }
  const ordered = byNumber
    .filter((t) => t.parentId === null)
    .flatMap((t) => [t, ...(childrenOf.get(t.id) ?? [])]);

  const derived = (task: TemplateSourceTask) =>
    (childrenOf.get(task.id) ?? []).some((c) => c.startDate !== null || c.dueDate !== null);
  const normalize = (date: string | null) =>
    date === null ? null : nearestWorkingDay(date, region);
  const dated = ordered.map((task) => {
    if (derived(task)) return { task, start: null, due: null };
    if (task.type === 'milestone') {
      const day = normalize(task.dueDate ?? task.startDate);
      return { task, start: day, due: day };
    }
    return { task, start: normalize(task.startDate), due: normalize(task.dueDate) };
  });

  const allDates = dated.flatMap((d) => [d.start, d.due]).filter((d): d is string => d !== null);
  const dayZero = allDates.length === 0 ? null : allDates.reduce((a, b) => (a < b ? a : b));
  const offset = (date: string | null) =>
    date === null || dayZero === null ? null : countWorkingDays(dayZero, date, region) - 1;

  const taskKeys = new Map(ordered.map((t, i) => [t.id, `t${i + 1}`]));
  const labelKeys = new Map(source.labels.map((l, i) => [l.id, `l${i + 1}`]));

  return {
    version: 1,
    holidayRegion: region,
    statuses: source.statuses.map((s) => ({
      name: s.name,
      category: s.category,
      color: s.color.toLowerCase(),
      wipLimit: s.wipLimit,
    })),
    labels: source.labels.map((l) => ({
      key: labelKeys.get(l.id) ?? '',
      name: l.name,
      color: l.color.toLowerCase(),
    })),
    tasks: dated.map(({ task, start, due }) => ({
      key: taskKeys.get(task.id) ?? '',
      parentKey: task.parentId === null ? null : (taskKeys.get(task.parentId) ?? null),
      type: task.type,
      title: task.title,
      description: task.description,
      priority: task.priority,
      estimateHours: task.estimateHours,
      labelKeys: task.labelIds.flatMap((id) => labelKeys.get(id) ?? []),
      startOffset: offset(start),
      dueOffset: offset(due),
    })),
    dependencies: source.dependencies.flatMap((d) => {
      const predecessorKey = taskKeys.get(d.predecessorId);
      const successorKey = taskKeys.get(d.successorId);
      if (predecessorKey === undefined || successorKey === undefined) return [];
      return [{ predecessorKey, successorKey, type: d.type, lagDays: d.lagDays }];
    }),
  };
}

// --- Applying a template (PROJ-07) -------------------------------------------------------------

/**
 * Where the template is placed: its first date (`start`), its last date (`end`) or one of its
 * milestones on `date`. The date moves to the next working day (`end`: the previous one).
 */
export const templateAnchorSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('start'), date: isoDateSchema }),
  z.object({ kind: z.literal('end'), date: isoDateSchema }),
  z.object({ kind: z.literal('milestone'), milestoneKey: localKeySchema, date: isoDateSchema }),
]);
export type TemplateAnchor = z.infer<typeof templateAnchorSchema>;

export type TemplateSchedule = {
  /** Dates per task key; derived parents get the range of their subtasks. */
  dates: Record<string, TaskDates>;
  /** First to last date, null if the template has no dates. */
  range: { startDate: string; dueDate: string } | null;
};

type ScheduledContent = Pick<ProjectTemplateContent, 'tasks'>;

/** Milestones with a date, usable as anchor. */
export function templateMilestones(content: ScheduledContent): TemplateTask[] {
  return content.tasks.filter((t) => t.type === 'milestone' && t.dueOffset !== null);
}

/** Dates of all tasks for an anchor; null if the anchor names no dated milestone. */
export function instantiateTemplate(
  content: ScheduledContent,
  anchor: TemplateAnchor,
  region: HolidayRegion,
): TemplateSchedule | null {
  const offsets = content.tasks.flatMap((t) =>
    [t.startOffset, t.dueOffset].filter((o): o is number => o !== null),
  );
  const last = offsets.length === 0 ? 0 : Math.max(...offsets);

  let dayZero: string;
  if (anchor.kind === 'start') {
    dayZero = nearestWorkingDay(anchor.date, region);
  } else if (anchor.kind === 'end') {
    dayZero = addWorkingDays(nearestWorkingDay(anchor.date, region, 'backward'), -last, region);
  } else {
    const milestone = templateMilestones(content).find((t) => t.key === anchor.milestoneKey);
    if (milestone?.dueOffset == null) return null;
    dayZero = addWorkingDays(nearestWorkingDay(anchor.date, region), -milestone.dueOffset, region);
  }
  const place = (o: number | null) => (o === null ? null : addWorkingDays(dayZero, o, region));

  const dates: Record<string, TaskDates> = {};
  for (const task of content.tasks) {
    dates[task.key] = { startDate: place(task.startOffset), dueDate: place(task.dueOffset) };
  }
  for (const task of content.tasks) {
    const children = content.tasks.filter((c) => c.parentKey === task.key);
    if (children.length === 0) continue;
    const { range } = summarizeSubtasks(
      children.map((c) => ({
        ...(dates[c.key] ?? { startDate: null, dueDate: null }),
        progress: 0,
        category: 'todo',
      })),
      region,
    );
    if (range !== null) dates[task.key] = range;
  }

  return {
    dates,
    range:
      offsets.length === 0
        ? null
        : { startDate: dayZero, dueDate: addWorkingDays(dayZero, last, region) },
  };
}

// --- API ---------------------------------------------------------------------------------------

export const templateNameSchema = z.string().trim().min(1).max(100);
export const templateDescriptionSchema = z.string().trim().max(1000);

export const templateIdParamsSchema = z.object({ templateId: z.uuid() });

export const projectTemplateSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  taskCount: z.number().int(),
  /** Only if the viewer can see the source project. */
  sourceProject: z.object({ id: z.uuid(), key: z.string(), name: z.string() }).nullable(),
  updatedAt: timestampSchema,
});
export type ProjectTemplateSummary = z.infer<typeof projectTemplateSummarySchema>;

export const projectTemplateListResponseSchema = z.object({
  items: z.array(projectTemplateSummarySchema),
});
export type ProjectTemplateListResponse = z.infer<typeof projectTemplateListResponseSchema>;

export const projectTemplateSchema = projectTemplateSummarySchema.extend({
  content: projectTemplateContentSchema,
});
export type ProjectTemplate = z.infer<typeof projectTemplateSchema>;

/** PROJ-06. An existing name is a 409 unless `replace` is set (the UI asks first). */
export const saveTemplateBodySchema = z.object({
  name: templateNameSchema,
  description: templateDescriptionSchema.nullable().optional(),
  replace: z.boolean().default(false),
});
export type SaveTemplateBody = z.infer<typeof saveTemplateBodySchema>;
export type SaveTemplateInput = z.input<typeof saveTemplateBodySchema>;

/** PROJ-08. */
export const updateTemplateBodySchema = z.object({
  name: templateNameSchema.optional(),
  description: templateDescriptionSchema.nullable().optional(),
});
export type UpdateTemplateBody = z.infer<typeof updateTemplateBodySchema>;

/** PROJ-07. */
export const applyTemplateBodySchema = z.object({
  templateId: z.uuid(),
  anchor: templateAnchorSchema,
});
export type ApplyTemplateBody = z.infer<typeof applyTemplateBodySchema>;

export const applyTemplateResponseSchema = z.object({ taskCount: z.number().int() });
export type ApplyTemplateResponse = z.infer<typeof applyTemplateResponseSchema>;
